import React, { useState } from "react";
import styles from "./CareerForm.module.css";
import useGeoLocation from "@/services/useGeoLocation";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css"; // Ensure you import the PhoneInput styles
import validator from "validator";
import { postCareerUserData } from "@/services/CmsService";

interface CareerFormProps {
  onFormSubmit?: () => void;
  formTittle?: string;
  formButton?: string;
}

// Custom hook for managing form errors
const useFormError = () => {
  const [errors, setErrors] = useState<Record<string, string | null>>({});

  const setError = (field: string, message: string | null) => {
    setErrors((prevErrors) => ({ ...prevErrors, [field]: message }));
  };

  const clearError = (field: string) => {
    setError(field, null);
  };

  const clearAllErrors = () => {
    setErrors({});
  };

  return { errors, setError, clearError, clearAllErrors };
};

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const CareerForm: React.FC<CareerFormProps> = ({
  onFormSubmit,
  formTittle,
  formButton,
}) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [resume, setResume] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { errors, setError, clearError, clearAllErrors } = useFormError();
  const geoLocationData: any = useGeoLocation();

  const resetForm = () => {
    setName("");
    setEmail("");
    setPhoneNumber("");
    setResume(null);
    clearAllErrors();
  };

  const validateForm = () => {
    let isValid = true;

    if (!name.trim() || !/^[a-zA-Z\s]+$/.test(name)) {
      setError("name", "Please enter a valid name (letters and spaces only).");
      isValid = false;
    } else {
      clearError("name");
    }

    if (!validator.isEmail(email)) {
      setError("email", "Please enter a valid email address.");
      isValid = false;
    } else {
      clearError("email");
    }

    if (!validator.isMobilePhone(phoneNumber || "")) {
      setError("phone", "Please enter a valid phone number.");
      isValid = false;
    } else {
      clearError("phone");
    }

    if (!resume) {
      setError("resume", "Please upload your resume.");
      isValid = false;
    } else if (resume.size > MAX_FILE_SIZE) {
      setError("resume", "Resume file size must be less than 5MB.");
      isValid = false;
    } else if (resume.type !== "application/pdf") {
      setError("resume", "Please upload a PDF file.");
      isValid = false;
    } else {
      clearError("resume");
    }

    return isValid;
  };

  const handleFormSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    clearAllErrors();

    try {
      if (resume) {
        // Convert file to base64
        const reader = new FileReader();
        reader.readAsDataURL(resume);

        reader.onload = async () => {
          try {
            const base64File = reader.result as string;

            const response = await fetch("/api/upload-resume", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                file: base64File,
                fileName: resume.name,
              }),
            });

            if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.message || "Failed to upload resume");
            }

            const result = await response.json();

            if (result.data?.publicUrl) {
              try {
                await postCareerUserData({
                  name,
                  email,
                  phoneNumber,
                  resumeUrl: result.data.publicUrl,
                });

                resetForm();
                onFormSubmit?.();
              } catch (error: any) {
                setError(
                  "resume",
                  `Failed to submit data: ${error.message || "Unknown error"}`
                );
              }
            } else {
              setError("resume", "Failed to upload resume: No URL received");
            }
          } catch (error: any) {
            setError(
              "resume",
              `An error occurred: ${error.message || "Unknown error"}`
            );
            console.error("Form submission error:", error);
          } finally {
            setIsSubmitting(false);
          }
        };

        reader.onerror = () => {
          setError("resume", "Failed to read file");
          setIsSubmitting(false);
        };
      }
    } catch (error: any) {
      setError(
        "resume",
        `An error occurred: ${error.message || "Unknown error"}`
      );
      console.error("Form submission error:", error);
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.formContainer}>
      <h1 className={styles.formTitle}>
        {formTittle ||
          " Submit your application today and become an integral part of our dynamic team!"}
      </h1>
      <form onSubmit={handleFormSubmit} className={styles.form}>
        <div className={styles.formGroup}>
          <input
            type="text"
            id="name"
            name="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Full Name"
            disabled={isSubmitting}
          />
          {errors.name && <small className={styles.error}>{errors.name}</small>}
        </div>

        <div className={styles.formGroup}>
          <input
            type="email"
            id="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            disabled={isSubmitting}
          />
          {errors.email && (
            <small className={styles.error}>{errors.email}</small>
          )}
        </div>

        <div className={styles.formGroup}>
          <div className={styles.fieldInPut}>
            <PhoneInput
              value={phoneNumber}
              id="phoneNumber"
              name="phoneNumber"
              onChange={(value) => setPhoneNumber(value || "")}
              international
              countryCallingCodeEditable={false}
              defaultCountry={geoLocationData?.countryCode || "IN"}
              placeholder="Phone Number"
              className={styles.phoneNumber}
              disabled={isSubmitting}
            />
          </div>
          {errors.phone && (
            <small className={styles.error}>{errors.phone}</small>
          )}
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="resume">Resume (PDF)</label>
          <input
            type="file"
            id="resume"
            name="resume"
            accept=".pdf"
            onChange={(e) =>
              setResume(e.target.files ? e.target.files[0] : null)
            }
            placeholder="Resume (PDF)"
            disabled={isSubmitting}
          />
          {errors.resume && (
            <small className={styles.error}>{errors.resume}</small>
          )}
        </div>

        <button
          type="submit"
          className={styles.submitButton}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Submitting..." : formButton || "Submit"}
        </button>
      </form>
    </div>
  );
};

export default CareerForm;
